import { Controller } from "@hotwired/stimulus"

const TRANSPARENT_PIXEL =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"

/** Remove Bootstrap modal leftovers that block clicks after Turbo / bfcache. */
function scrubBootstrapModalArtifacts() {
  document.body.classList.remove("modal-open")
  document.body.style.removeProperty("overflow")
  document.body.style.removeProperty("padding-right")
  document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove())
}

export default class extends Controller {
  static targets = ["image", "link", "ctaRow", "closeButton"]
  /** Delay before the CTA row expands (ms). No layout gap: row is collapsed until then. */
  static values = {
    ctaDelayMs: { type: Number, default: 3000 },
  }

  connect() {
    this.onOpen = this.onOpen.bind(this)
    this.onBeforeCache = this.onBeforeCache.bind(this)
    this.onDialogClose = this.onDialogClose.bind(this)
    this.onPageShow = this.onPageShow.bind(this)
    this.onTurboLoad = this.onTurboLoad.bind(this)

    document.addEventListener("mo:open-ad-modal", this.onOpen)
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
    document.addEventListener("turbo:load", this.onTurboLoad)
    window.addEventListener("pageshow", this.onPageShow)
    this.element.addEventListener("close", this.onDialogClose)

    this.ensureDialogClosedAndScrub()
  }

  disconnect() {
    document.removeEventListener("mo:open-ad-modal", this.onOpen)
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    document.removeEventListener("turbo:load", this.onTurboLoad)
    window.removeEventListener("pageshow", this.onPageShow)
    this.element.removeEventListener("close", this.onDialogClose)
    this.ensureDialogClosedAndScrub()
  }

  get effectiveCtaDelayMs() {
    if (typeof window === "undefined" || !window.matchMedia) return this.ctaDelayMsValue
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return 0
    return this.ctaDelayMsValue
  }

  clearCtaRevealTimer() {
    if (this._ctaRevealTimer) {
      clearTimeout(this._ctaRevealTimer)
      this._ctaRevealTimer = null
    }
  }

  resetCtaPresentation() {
    this.clearCtaRevealTimer()
    if (!this.hasCtaRowTarget) return
    this.ctaRowTarget.classList.remove(
      "mo-ad-dialog__footer--cta-wait",
      "mo-ad-dialog__footer--cta-reveal"
    )
  }

  scheduleCtaReveal() {
    this.clearCtaRevealTimer()
    const delay = this.effectiveCtaDelayMs
    this._ctaRevealTimer = setTimeout(() => {
      this._ctaRevealTimer = null
      if (!this.hasCtaRowTarget || !this.element.open) return
      this.ctaRowTarget.classList.remove("mo-ad-dialog__footer--cta-wait")
      this.ctaRowTarget.classList.add("mo-ad-dialog__footer--cta-reveal")
    }, delay)
  }

  ensureDialogClosedAndScrub() {
    scrubBootstrapModalArtifacts()
    this.resetCtaPresentation()
    if (this.element.open) this.element.close()
    this.resetContent()
  }

  onPageShow(event) {
    if (event.persisted) this.ensureDialogClosedAndScrub()
  }

  onTurboLoad() {
    this.ensureDialogClosedAndScrub()
  }

  onDialogClose() {
    this.resetCtaPresentation()
    this.resetContent()
  }

  onBeforeCache() {
    this.ensureDialogClosedAndScrub()
  }

  onOpen(event) {
    const { src, link, alt, showButton } = event.detail || {}
    if (!src) return

    this.resetCtaPresentation()

    this.imageTarget.src = src
    this.imageTarget.alt = alt || ""

    this.linkTargets.forEach((a) => {
      a.href = link || "#"
    })

    if (this.hasCtaRowTarget) {
      if (showButton) {
        this.ctaRowTarget.classList.remove("d-none")
        this.ctaRowTarget.classList.remove("mo-ad-dialog__footer--cta-reveal")
        this.ctaRowTarget.classList.add("mo-ad-dialog__footer--cta-wait")
        this.scheduleCtaReveal()
      } else {
        this.clearCtaRevealTimer()
        this.ctaRowTarget.classList.remove(
          "mo-ad-dialog__footer--cta-wait",
          "mo-ad-dialog__footer--cta-reveal"
        )
        this.ctaRowTarget.classList.add("d-none")
      }
    } else {
      this.clearCtaRevealTimer()
    }

    if (typeof this.element.showModal === "function") {
      this.element.showModal()
      queueMicrotask(() => this.closeButtonTarget?.focus?.())
    }

    if (window.matomoTracker) {
      window.matomoTracker.trackEvent("App", "impression")
    }
  }

  close() {
    if (this.element.open) this.element.close()
  }

  backdropClose(event) {
    if (event.target === this.element) this.close()
  }

  resetContent() {
    if (!this.hasImageTarget) return
    this.imageTarget.src = TRANSPARENT_PIXEL
    this.imageTarget.alt = ""
  }
}
